'use client'

// PromptWindow — the "prompt-injection window". Shows a block of prompt text
// the user copies wholesale (one big copy button, "copied ✓" feedback), with a
// caption telling them exactly where to paste it. The body reads as a quiet
// terminal-ish surface (mono, --surface-2 fill, soft hairline) so it's obviously
// a thing-to-copy and not prose. Reuses the navigator.clipboard pattern shared
// by CopyButton / the connect CopyLine.
//
// FOLDED, NEVER A SCROLLER (2026-09-28). A long prompt used to scroll inside
// its own capped box, which put a scroller inside the window's scroller: a
// wheel over the prompt latched onto it, stopped dead at its end, and the
// window only moved on the next gesture. With three prompts stacked they
// covered most of the window, so the whole thing felt stuck. The prompt now
// shows its opening lines, fading out, and opens IN PLACE to its full length
// ("read it all"); the one scroll is always the window's. Copy takes the whole
// prompt either way.
//
//   <PromptWindow
//     prompt={PROMPT_SYNC}
//     caption="paste this into your tool's custom instructions"
//     label="sync prompt" />

import { useEffect, useRef, useState } from 'react'
import { Label } from './Typography'

export default function PromptWindow({
  prompt,
  caption,
  label,
  maxHeight = 320,
}: {
  /** the full text the user copies */
  prompt: string
  /** where to paste it — rendered under the window */
  caption?: React.ReactNode
  /** small lowercase header above the window */
  label?: string
  /** the folded height of the prompt body (px); it opens in place past this */
  maxHeight?: number
}) {
  const [copied, setCopied] = useState(false)
  const [open, setOpen] = useState(false)
  // the prompt's own full height, so the fold eases to a real number
  const preRef = useRef<HTMLPreElement>(null)
  const [full, setFull] = useState<number | null>(null)
  useEffect(() => {
    const el = preRef.current
    if (!el) return
    const measure = () => setFull(el.scrollHeight)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [prompt])
  const folds = full != null && full > maxHeight + 8
  const fade = folds && !open ? 'linear-gradient(180deg, #000 55%, transparent)' : undefined

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(prompt)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      /* clipboard unavailable — surface nothing, the text is selectable */
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
      {label && <Label as="span" style={{ display: 'block' }}>{label}</Label>}

      <div
        style={{
          position: 'relative',
          background: 'var(--surface-2)',
          border: '1px solid var(--hair-soft)',
          borderRadius: 'var(--radius-sm)',
          overflow: 'hidden',
        }}
      >
        {/* the prompt body */}
        <pre
          ref={preRef}
          style={{
            margin: 0,
            padding: '1rem 1.1rem',
            paddingRight: '7.2rem',
            maxHeight: open && full ? full : maxHeight,
            overflow: 'hidden',
            WebkitMaskImage: fade,
            maskImage: fade,
            transition: 'max-height var(--dur) var(--ease-soft)',
            fontFamily: 'var(--font-mono)',
            fontSize: '0.76rem',
            lineHeight: 1.7,
            color: 'var(--ink-2)',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {prompt}
        </pre>

        {/* the big copy button, pinned top-right and always reachable */}
        <button
          className="chip"
          onClick={copy}
          data-on={copied || undefined}
          style={{
            position: 'absolute',
            top: '0.7rem',
            right: '0.7rem',
            fontSize: '0.72rem',
            padding: '0.35rem 0.85rem',
            background: 'color-mix(in oklab, var(--bg) 82%, transparent)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
          }}
        >
          {copied ? 'copied ✓' : 'copy prompt'}
        </button>
      </div>

      {(caption || folds) && (
        <div
          style={{
            display: 'flex',
            gap: '0.5rem',
            alignItems: 'baseline',
            fontSize: '0.8rem',
            color: 'var(--ink-3)',
            lineHeight: 1.55,
          }}
        >
          {caption && <span aria-hidden style={{ color: 'var(--accent-ink)', flexShrink: 0 }}>↳</span>}
          {caption && <span>{caption}</span>}
          {folds && (
            <button
              type="button"
              className="quiet-link"
              aria-expanded={open}
              onClick={() => setOpen((o) => !o)}
              style={{ marginLeft: 'auto', flexShrink: 0, fontSize: 'inherit' }}
            >
              {open ? 'fold it' : 'read it all'}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
